import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import './PaymentSuccessPage.css';

const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);
  const [phoneProvisioning, setPhoneProvisioning] = useState<'pending' | 'success' | 'error'>('pending');
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      console.log('⚠️ No session_id found, redirecting to pricing');
      navigate('/pricing');
      return;
    }

    console.log('✅ Valid payment session found:', sessionId);

    // Create subscription and provision phone after payment
    const setupAccount = async () => {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
          console.error('No user found for setup');
          setPhoneProvisioning('error');
          return;
        }

        console.log('💳 Setting up account for user:', user.id);

        // STEP 1: Create subscription in database
        try {
          const { data: profile } = await supabase
            .from('profiles')
            .select('team_id')
            .eq('id', user.id)
            .single();

          if (profile?.team_id) {
            console.log('📝 Creating subscription for team:', profile.team_id);

            // Fetch the Stripe checkout session to get subscription details
            const API_URL = import.meta.env.VITE_API_URL || 'https://homequest-api-1.onrender.com';
            let trialEnd: Date | null = null;
            let stripeSubscriptionId = sessionId;

            try {
              const sessionResponse = await fetch(`${API_URL}/api/payments/session/${sessionId}`);
              if (sessionResponse.ok) {
                const sessionData = await sessionResponse.json();
                if (sessionData.success && sessionData.data.subscription) {
                  stripeSubscriptionId = sessionData.data.subscription.id;
                  // If there's a trial, use trial_end, otherwise use current_period_end
                  trialEnd = sessionData.data.subscription.trial_end
                    ? new Date(sessionData.data.subscription.trial_end * 1000)
                    : new Date(sessionData.data.subscription.current_period_end * 1000);
                  console.log('📅 Subscription period end:', trialEnd);
                }
              }
            } catch (fetchError) {
              console.warn('Could not fetch Stripe session, using default period:', fetchError);
            }

            // Get tier from URL or localStorage
            const tier = searchParams.get('tier') || localStorage.getItem('selected_tier') || 'professional';

            // Use Stripe trial_end if available, otherwise default to 14 days
            const periodEnd = trialEnd || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);

            const { error: subError } = await supabase
              .from('subscriptions')
              .upsert({
                team_id: profile.team_id,
                tier: tier,
                status: 'active',
                stripe_customer_id: sessionId,
                stripe_subscription_id: stripeSubscriptionId,
                current_period_end: periodEnd.toISOString(),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              }, {
                onConflict: 'team_id'
              });

            if (subError) {
              console.error('❌ Subscription creation error:', subError);
            } else {
              console.log('✅ Subscription created successfully!');
              // Store flag in localStorage so ProtectedRoute knows subscription exists
              const timestamp = Date.now().toString();
              localStorage.setItem('subscription_just_created', timestamp);
              console.log('🎯 Subscription flag set:', timestamp);
            }
          }
        } catch (err) {
          console.error('Error creating subscription:', err);
        }

        // STEP 2: Provision phone number
        console.log('📞 Provisioning phone for user:', user.id);

        // Call backend API to provision phone
        const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://homequest-api-1.onrender.com'}/api/provision-phone`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId: user.id
          })
        });

        const result = await response.json();

        if (result.success && result.data?.phoneConfig?.twilioNumber) {
          console.log('✅ Phone provisioned:', result.data.phoneConfig.twilioNumber);
          setPhoneNumber(result.data.phoneConfig.twilioNumber);
          setPhoneProvisioning('success');
        } else {
          console.error('Phone provisioning failed:', result);
          setPhoneProvisioning('error');
        }
      } catch (error) {
        console.error('Error provisioning phone:', error);
        setPhoneProvisioning('error');
      }
    };

    setupAccount();

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/dashboard');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, searchParams]);

  return (
    <div className="payment-success-page">
      <div className="success-container">
        <div className="success-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>

        <h1 className="success-title">Payment Successful!</h1>
        <p className="success-message">
          Thank you for subscribing to HomeQuest Tech™. Your account has been upgraded and
          you now have access to all your tier features.
        </p>

        <div className="success-details">
          <div className="detail-item">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 12l2 2 4-4" />
              <circle cx="12" cy="12" r="10" />
            </svg>
            <span>Subscription Activated</span>
          </div>
          <div className="detail-item">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>Confirmation Email Sent</span>
          </div>
          <div className="detail-item">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
            </svg>
            <span>
              {phoneProvisioning === 'pending' && 'Provisioning Phone Number...'}
              {phoneProvisioning === 'success' && `Phone: ${phoneNumber}`}
              {phoneProvisioning === 'error' && 'Phone Setup Available in Dashboard'}
            </span>
          </div>
        </div>

        <div className="redirect-notice">
          <p>Redirecting to dashboard in {countdown} seconds...</p>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${((5 - countdown) / 5) * 100}%` }}
            />
          </div>
        </div>

        <button className="dashboard-button" onClick={() => navigate('/dashboard')}>
          Go to Dashboard Now
        </button>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
